/**
 * App-wide toast notifications (replaces window.alert and ad-hoc toasts).
 *
 *   import { toast } from '@/utils/toast';
 *   toast.error('Could not rename chat');
 *   const id = toast.show({ message: 'Chat deleted', action: { label: 'Undo', onClick: restore }, duration: 6000 });
 *   toast.dismiss(id);
 *
 * Rendered by <ToastHost> (mounted once in App.vue). Max 3 visible; newest on
 * top; hover pauses auto-dismiss; error toasts are announced assertively.
 */
import { reactive } from 'vue';

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = { info: 4000, success: 3000, error: 6000 };

export const toastState = reactive({ items: [] });

let seq = 0;
const timers = new Map();

function schedule(item) {
  clearTimeout(timers.get(item.id));
  if (!item.duration || item.duration === Infinity) return;
  timers.set(item.id, setTimeout(() => dismiss(item.id), item.remaining));
  item.startedAt = Date.now();
}

export function dismiss(id) {
  clearTimeout(timers.get(id));
  timers.delete(id);
  const idx = toastState.items.findIndex((t) => t.id === id);
  if (idx !== -1) {
    const [item] = toastState.items.splice(idx, 1);
    if (item.onDismiss) {
      try { item.onDismiss(); } catch (e) { console.error(e); }
    }
  }
}

/**
 * @param {{message: string, kind?: 'info'|'success'|'error', duration?: number,
 *          action?: {label: string, onClick: Function}, onDismiss?: Function, key?: string}} opts
 *   `key` de-duplicates: showing a toast with the same key replaces the old one.
 * @returns {number} toast id
 */
export function show(opts) {
  const kind = opts.kind || 'info';
  if (opts.key) {
    const existing = toastState.items.find((t) => t.key === opts.key);
    if (existing) dismiss(existing.id);
  }
  seq += 1;
  const duration = opts.duration != null ? opts.duration : DEFAULT_DURATION[kind];
  const item = {
    id: seq,
    key: opts.key || null,
    kind,
    message: String(opts.message || ''),
    action: opts.action || null,
    onDismiss: opts.onDismiss || null,
    duration,
    remaining: duration,
    startedAt: 0,
  };
  toastState.items.unshift(item);
  while (toastState.items.length > MAX_VISIBLE) dismiss(toastState.items[toastState.items.length - 1].id);
  schedule(item);
  return item.id;
}

export function pause(id) {
  const item = toastState.items.find((t) => t.id === id);
  if (!item || !item.startedAt) return;
  clearTimeout(timers.get(id));
  item.remaining = Math.max(800, item.remaining - (Date.now() - item.startedAt));
  item.startedAt = 0;
}

export function resume(id) {
  const item = toastState.items.find((t) => t.id === id);
  if (item && !item.startedAt) schedule(item);
}

export const toast = {
  show,
  dismiss,
  info: (message, opts = {}) => show({ ...opts, message, kind: 'info' }),
  success: (message, opts = {}) => show({ ...opts, message, kind: 'success' }),
  error: (message, opts = {}) => show({ ...opts, message, kind: 'error' }),
};
